import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import OrganizationService from '../../services/organizationService';
import RoleService from '../../services/roleService';
import Button from '../common/Button';
import AddUserModal from './AddUserModal';
import './OrganizationUsers.css';

const OrganizationUsers = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [orgRoles, setOrgRoles] = useState([]);
    const [roleError, setRoleError] = useState(null);

    const fetchOrganizationUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const userId = auth.user?.profile?.sub;
            const token = auth.user?.access_token;

            if (!userId || !token) {
                setError('No se pudo obtener la información de autenticación');
                return;
            }

            // Get current user details first
            const currentUserData = await UserService.getUserById(userId, token);
            setCurrentUser(currentUserData);

            // Get organization details
            if (currentUserData?.organizationId) {
                const orgData = await OrganizationService.getOrganizationById(currentUserData.organizationId, token);
                setOrganization(orgData);
            }

            // Get all organization users
            const organizationUsers = await UserService.getOrganizationUsers(token);
            setUsers([...organizationUsers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')));
        } catch (err) {
            console.error('Error fetching organization users:', err);
            setError(err.message || 'Error al cargar los usuarios de la organización');
        } finally {
            setIsLoading(false);
        }
    }, [auth.user]);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user) {
            fetchOrganizationUsers();
        }
    }, [auth.isAuthenticated, auth.user, fetchOrganizationUsers]);

    const getRoleDisplayName = (appRole, customRoleName) => {
        switch (appRole) {
            case 'owner':
                return 'Propietario';
            case 'admin':
                return 'Administrador';
            case 'user':
                return customRoleName || 'Usuario';
            default:
                return 'Usuario';
        }
    };

    const handleCreateUser = async () => {
        if (isAtUserLimit) {
            setRoleError(`Has alcanzado el límite de ${userLimit} usuarios para tu organización.`);
            return;
        }

        const token = auth.user?.access_token;
        try {
            const response = await RoleService.getAllRoles(token);
            setOrgRoles(response.data || []);
        } catch {
            setOrgRoles([]);
        }
        setShowAddUserModal(true);
    };

    const handleAddUser = async (userData) => {
        setIsAddingUser(true);
        setRoleError(null);

        console.log('User data to provision:', userData);

        try {
            if (!currentUser?.organizationId) {
                throw new Error('No se pudo determinar la organización. Por favor recarga la página.');
            }

            if (activeUsersCount >= userLimit) {
                throw new Error(`Has alcanzado el límite de ${userLimit} usuarios para tu organización.`);
            }

            await UserService.provisionUser({
                ...userData,
                organizationId: currentUser.organizationId
            });

            setMessage({
                type: 'success',
                text: 'Usuario agregado correctamente. Se ha enviado un correo de invitación.'
            });

            // Close modal and refresh users list
            setShowAddUserModal(false);
            setRoleError(null);
            await fetchOrganizationUsers();
        } catch (err) {
            console.error('Error adding user:', err);
            setRoleError(err.message || 'Error al agregar el usuario');
        } finally {
            setIsAddingUser(false);
        }
    };

    const handleCloseModal = () => {
        if (!isAddingUser) {
            setShowAddUserModal(false);
            setRoleError(null);
        }
    };

    if (isLoading) {
        return (
            <div className="organization-users-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="organization-users-container">
                <div className="error-state">
                    <i className="fas fa-exclamation-circle"></i>
                    <p>{error}</p>
                    <Button onClick={fetchOrganizationUsers}>Reintentar</Button>
                </div>
            </div>
        );
    }



    const activeUsersCount = users.filter(user => user.active !== false).length;
    const userLimit = organization?.allowedUsers || 3;
    const isAtUserLimit = activeUsersCount >= userLimit;

    return (
        <div className="organization-users-container">
            {/* Header */}
            <div className="organization-users-header">
                <div className="header-content">
                    <h1>Usuarios</h1>
                    <p>Gestiona los usuarios y sus roles dentro de tu organización</p>
                </div>
                {(currentUser?.appRole === 'owner' || currentUser?.appRole === 'admin') && (
                    <button
                        className="btn-primary add-btn"
                        onClick={handleCreateUser}
                        disabled={isAtUserLimit}
                        title={isAtUserLimit ? `Has alcanzado el límite de ${userLimit} usuarios` : 'Agregar un nuevo usuario'}
                    >
                        {isAtUserLimit ? 'Límite alcanzado' : 'Agregar Usuario'}
                    </button>
                )}
            </div>

            {/* Seat Counter Banner */}
            {organization && (
                <div className="seat-counter-banner">
                    <div className="seat-counter-content">
                        <i className="fas fa-users"></i>
                        <span className="seat-counter-text">
                            <strong>{activeUsersCount}</strong> de <strong>{userLimit}</strong> usuarios activos
                        </span>
                    </div>
                    <div className="seat-counter-bar">
                        <div
                            className="seat-counter-fill"
                            style={{
                                width: `${Math.min((activeUsersCount / userLimit) * 100, 100)}%`
                            }}
                        ></div>
                    </div>
                </div>
            )}

            {/* Message */}
            {
                message.text && (
                    <div className={`organization-message ${message.type}`}>
                        <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                        <span>{message.text}</span>
                    </div>
                )
            }

            {/* Users Table */}
            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>
                                Rol
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id} onClick={() => navigate(`/settings/users/${user.id}`)} className={`clickable-row ${user.active === false ? 'inactive-row' : ''}`}>
                                <td>
                                    <div className="organization-user-info">
                                        <div className="organization-user-name-line">
                                            <span className="organization-user-name">{user.name}</span>
                                            {user.id === currentUser?.id && (
                                                <span className="current-user-badge">Tú</span>
                                            )}
                                            {user.active === false && (
                                                <span className="inactive-user-badge">Inactivo</span>
                                            )}
                                        </div>
                                        <div className="organization-user-email">{user.email}</div>
                                    </div>
                                </td>
                                <td>
                                    <div className="role-display">
                                        {getRoleDisplayName(user.appRole || 'user', user.customRoleName)}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add User Modal */}
            <AddUserModal
                isOpen={showAddUserModal}
                onClose={handleCloseModal}
                onSubmit={handleAddUser}
                loading={isAddingUser}
                orgRoles={orgRoles}
                roleError={roleError}
                onRoleErrorClear={() => setRoleError(null)}
            />
        </div >
    );
};

export default OrganizationUsers;
